import { Suspense, useEffect, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import SEO from './components/SEO';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Navigation } from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage';
import AlternativeHomePage from './pages/AlternativeHomePage';
import CampPage from './pages/CampPage';
import CampsByCityPage from './pages/CampsByCityPage';
import StatePage from './pages/StatePage';
import CityPage from './pages/CityPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import SignInPage from './pages/SignInPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import KidsSchedule from './pages/Calendar';
import PublicCalendarView from './pages/PublicCalendarView';
import FavoriteCampsPage from './pages/FavoriteCampsPage';
import CampDiariesPage from './pages/CampDiariesPage';
import PricingOwnersPage from './pages/PricingOwnersPage';
import PricingParentsPage from './pages/PricingParentsPage';
import PricingRedirectPage from './pages/PricingRedirectPage';
import ClaimSuccessPage from './pages/ClaimSuccessPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import MyCampTeaserPage from './pages/MyCampTeaserPage';
import ForParentsPage from './pages/ForParentsPage';
import ForCampOwnersPage from './pages/ForCampOwnersPage';
import ParentToolsPage from './pages/ParentToolsPage';
import SpellingBeePage from './pages/SpellingBeePage';
import LTDParentsPage from './pages/LTDParentsPage';
import LTDParentsVideoPage from './pages/LTDParentsVideoPage';
import LTDOwnersPage from './pages/LTDOwnersPage';
import LTDOwnersSuccessPage from './pages/LTDOwnersSuccessPage';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { supabase } from './lib/supabase';
import CookieConsent from './components/CookieConsent';
import PostHogProvider from './components/PostHogProvider';
import CrispProvider from './components/CrispProvider';
import { ThemeProvider, useTheme } from './lib/theme/index.js';

// Lazy load admin pages to prevent MDX editor from being bundled in SSR
const AdminPage = lazy(() => import('./pages/AdminPage'));
const EnrichmentLogsPage = lazy(() => import('./pages/EnrichmentLogsPage'));
const CampDiaryEditorPage = lazy(() => import('./pages/CampDiaryEditorPage'));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const NotFoundPage = () => {
  const { currentTheme, themeVariant } = useTheme();
  const campingQuotes = [
    "Looks like this trail leads nowhere! 🏕️",
    "Oops! This campsite seems to be off the map! ⛺",
    "Houston, we've wandered off the nature path! 🌲",
    "This s'more recipe page got toasted! 🔥",
    "Even our camp compass couldn't find this page! 🧭"
  ];

  const randomQuote = campingQuotes[Math.floor(Math.random() * campingQuotes.length)];

  return (
    <>
      <SEO
        title="Page Not Found | CampWhiz"
        description="Oops! This page seems to be off the trail. Head back to base camp to find summer camps and activities for your kids."
        url="/404"
        noindex={true}
      />
      <div className="min-h-screen flex items-center justify-center p-4" data-theme={themeVariant}>
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
        
        <div className="mb-8">
          <span className="text-8xl mb-4 block">🏕️</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
          <p className="text-xl font-medium text-gray-800 mb-2">{randomQuote}</p>
          <p className="text-gray-600">Don't worry, even the best campers get lost sometimes!</p>
        </div>

        <div className="space-y-4">
          <a
            href="/"
            className={`inline-block ${currentTheme.buttonBg} text-white px-6 py-3 rounded-md ${currentTheme.buttonHover} transition-colors`}
          >
            Back to Base Camp
          </a>
          
          <div className="text-sm text-gray-500 flex items-center justify-center space-x-2">
            <span>⛺</span>
            <span>🌲</span>
            <span>🔥</span>
            <span>🎣</span>
            <span>🏃‍♂️</span>
          </div>
        </div>

        <div className="mt-8 text-sm text-gray-500">
          <p>Need help finding your way?</p>
          <p>Check out our <a href="/camps-by-city" className="text-primary hover:text-primary/80 hover:underline">camp directory</a>!</p>
        </div>
      </div>
      </div>
    </>
  );
};

const AppContent = () => {
  const { currentTheme, themeVariant } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Send Umami pageview on route change so visit duration is calculated (SPA has no full reloads)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.umami?.track) return;
    const url = location.pathname + (location.search || '');
    window.umami.track((props) => ({ ...props, url, title: document.title }));
  }, [location.pathname, location.search]);

  // Supabase's detectSessionInUrl: true handles OAuth token detection automatically
  // We only redirect to /auth/callback for email confirmations that need special processing
  useEffect(() => {
    if (location.pathname !== '/auth/callback' && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      // Only redirect for email confirmations that need user type processing
      // OAuth is handled automatically by Supabase's detectSessionInUrl
      if (type === 'signup') {
        console.log('🔐 [AUTH] Email confirmation detected, redirecting to /auth/callback');
        navigate('/auth/callback', { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  const isAltHome = location.pathname === '/' || location.pathname === '/map-search';
  const isParentTools = location.pathname.startsWith('/parent-tools');
  return (
  <div className="flex flex-col min-h-screen" data-theme={themeVariant}>
    <ScrollToTop />
    {!isParentTools && (
      <header role="banner" className="site-header">
        <Navigation />
      </header>
    )}

            <main role="main" className={
              isAltHome ? 'flex-grow w-full' : 
              isParentTools ? 'flex-grow w-full' : 
              'flex-grow container mx-auto px-4 py-12'
            }>
      <Routes>
        <Route path="/" element={
          <Suspense fallback={<LoadingFallback />}>
            <AlternativeHomePage />
          </Suspense>
        } />
        <Route path="/search" element={
          <Suspense fallback={<LoadingFallback />}>
            <HomePage />
          </Suspense>
        } />
        <Route path="/search-v2" element={
          <Suspense fallback={<LoadingFallback />}>
            <AlternativeHomePage />
          </Suspense>
        } />
        <Route path="/map-search" element={
          <Suspense fallback={<LoadingFallback />}>
            <AlternativeHomePage />
          </Suspense>
        } />
        <Route path="/camps/*" element={
          <Suspense fallback={<LoadingFallback />}>
            <CampPage />
          </Suspense>
        } />
        <Route path="/camps-by-city" element={
          <Suspense fallback={<LoadingFallback />}>
            <CampsByCityPage />
          </Suspense>
        } />
        <Route path="/camps-by-city/:stateSlug" element={
          <Suspense fallback={<LoadingFallback />}>
            <StatePage />
          </Suspense>
        } />
        <Route path="/camps-by-city/:stateSlug/:citySlug" element={
          <Suspense fallback={<LoadingFallback />}>
            <CityPage />
          </Suspense>
        } />
        <Route path="/camp-diaries" element={
          <Suspense fallback={<LoadingFallback />}>
            <CampDiariesPage />
          </Suspense>
        } />
        <Route path="/camp-diaries/:slug" element={
          <Suspense fallback={<LoadingFallback />}>
            <CampDiariesPage />
          </Suspense>
        } />
        <Route path="/privacy" element={
          <Suspense fallback={<LoadingFallback />}>
            <PrivacyPage />
          </Suspense>
        } />
        <Route path="/about" element={
          <Suspense fallback={<LoadingFallback />}>
            <AboutPage />
          </Suspense>
        } />
        <Route path="/register" element={
          <Suspense fallback={<LoadingFallback />}>
            <SignInPage />
          </Suspense>
        } />
        <Route path="/join" element={
          <Suspense fallback={<LoadingFallback />}>
            <SignInPage />
          </Suspense>
        } />
        <Route path="/login" element={
          <Suspense fallback={<LoadingFallback />}>
            <SignInPage />
          </Suspense>
        } />
        <Route path="/auth/callback" element={
          <Suspense fallback={<LoadingFallback />}>
            <AuthCallbackPage />
          </Suspense>
        } />
        <Route path="/pricing" element={
          <Suspense fallback={<LoadingFallback />}>
            <PricingRedirectPage />
          </Suspense>
        } />
        <Route path="/pricing/owners" element={
          <Suspense fallback={<LoadingFallback />}>
            <PricingOwnersPage />
          </Suspense>
        } />
        <Route path="/pricing/parents" element={
          <Suspense fallback={<LoadingFallback />}>
            <PricingParentsPage />
          </Suspense>
        } />
        <Route path="/ltd/parents" element={
          <Suspense fallback={<LoadingFallback />}>
            <LTDParentsPage />
          </Suspense>
        } />
        <Route path="/ltd/parents-video" element={
          <Suspense fallback={<LoadingFallback />}>
            <LTDParentsVideoPage />
          </Suspense>
        } />
        <Route path="/ltd/owners" element={
          <Suspense fallback={<LoadingFallback />}>
            <LTDOwnersPage />
          </Suspense>
        } />
        <Route path="/ltd/owners/success" element={
          <LTDOwnersSuccessPage />
        } />
        <Route path="/kids-schedule" element={
          <Suspense fallback={<LoadingFallback />}>
            <KidsSchedule />
          </Suspense>
        } />
        <Route path="/calendar/share/:token" element={
          <Suspense fallback={<LoadingFallback />}>
            <PublicCalendarView />
          </Suspense>
        } />
        <Route path="/for-parents" element={
          <Suspense fallback={<LoadingFallback />}>
            <ForParentsPage />
          </Suspense>
        } />
        <Route path="/for-camp-owners" element={
          <Suspense fallback={<LoadingFallback />}>
            <ForCampOwnersPage />
          </Suspense>
        } />
        <Route path="/favorites" element={
          <Suspense fallback={<LoadingFallback />}>
            <FavoriteCampsPage />
          </Suspense>
        } />
        <Route path="/admin" element={
          <Suspense fallback={<LoadingFallback />}>
            <AdminPage />
          </Suspense>
        } />
        <Route path="/enrichment-logs" element={
          <Suspense fallback={<LoadingFallback />}>
            <EnrichmentLogsPage />
          </Suspense>
        } />
        <Route path="/admin/camp-diary/:id" element={
          <Suspense fallback={<LoadingFallback />}>
            <CampDiaryEditorPage />
          </Suspense>
        } />
        <Route path="/claim/success" element={
          <Suspense fallback={<LoadingFallback />}>
            <ClaimSuccessPage />
          </Suspense>
        } />
        <Route path="/payment/success" element={
          <Suspense fallback={<LoadingFallback />}>
            <PaymentSuccessPage />
          </Suspense>
        } />
        <Route path="/mycamp-teaser" element={
          <Suspense fallback={<LoadingFallback />}>
            <MyCampTeaserPage />
          </Suspense>
        } />
        <Route path="/parent-tools" element={
          <Suspense fallback={<LoadingFallback />}>
            <ParentToolsPage />
          </Suspense>
        } />
        <Route path="/parent-tools/spelling-bee" element={
          <Suspense fallback={<LoadingFallback />}>
            <SpellingBeePage />
          </Suspense>
        } />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </main>

    {!isParentTools && (
      <footer role="contentinfo" className="site-footer">
        <Footer />
      </footer>
    )}

    {/* PostHog Test Component - Remove this after testing */}


    <CookieConsent />
    <Toaster 
      position="top-center"
      containerClassName="toast-container"
      containerStyle={{
        top: '5rem', // Position below navigation (h-20 = 5rem = 80px)
        zIndex: 10002, // Ensure toasts appear above all modals (modals use z-[9999], revoke confirmation uses z-[10001])
        position: 'fixed', // Ensure fixed positioning
      }}
      toastOptions={{ 
        style: { 
          zIndex: 10002, // High z-index to appear above modals and revoke confirmation
          marginTop: '0.5rem', // Small additional spacing
          position: 'relative', // Relative to container
        } 
      }} 
    />
  </div>
  );
};

export default Sentry.withErrorBoundary(function App() {
  console.log('🔍 [App] Rendering App');
  return (
    <SessionContextProvider supabaseClient={supabase}>
      <QueryClientProvider client={queryClient}>
        {/* Use the HelmetProvider provided at the server boundary; avoid duplicating here */}
        <PostHogProvider>
          <CrispProvider>
            <ThemeProvider>
              <AppContent />
            </ThemeProvider>
          </CrispProvider>
        </PostHogProvider>
      </QueryClientProvider>
    </SessionContextProvider>
  );
}, {
  fallback: ({ error, resetError }) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-600 mb-4">We're sorry for the inconvenience. The error has been reported.</p>
        <button
          onClick={resetError}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  ),
  showDialog: true,
});